import React, { useEffect, useRef, useState } from 'react';
import { UncontrolledTooltip } from 'reactstrap';


const officeBlockLabel = (block) => {
	if (block === 'AM') return 'Morning (AM)';
	if (block === 'PM') return 'Afternoon (PM)';
	return block;
};

const selectionKey = (times) => (times || []).slice().sort().join(',');

export default function ScheduleBlocks({
	disabled = false,
	blocks,
	returnSelected,
	isEngineer = false,
	selected = [],
	initialBlocks,
	blockLabel = null,
	dayName = null,
	lockedBlocks = [],
	lockReasons = null,
}) {
	const [selectedTimes, setSelectedTimes] = useState(() => {
		return new Set((selected || []).map((b) => b));
	});

	const lastReportedKey = useRef(selectionKey(selected));
	const selectedKey = selectionKey(selected);

	// Sync down from parent only when the selection content actually changes.
	useEffect(() => {
		if (selectionKey([...selectedTimes]) === selectedKey) {
			return;
		}
		setSelectedTimes(new Set((selected || []).map((b) => b)));
		lastReportedKey.current = selectedKey;
	}, [selectedKey]);

	// Notify parent only when local selection content changes (avoids render loops).
	useEffect(() => {
		const reported = blocks.filter((block) => selectedTimes.has(block));
		const key = selectionKey(reported);
		if (key === lastReportedKey.current) {
			return;
		}
		lastReportedKey.current = key;
		if (typeof returnSelected === 'function') {
			returnSelected(reported);
		}
	}, [selectedTimes, blocks]);

	const handleClick = (time) => () => {
		if (disabled || lockedBlocks.includes(time)) {
			return;
		}
		setSelectedTimes((prev) => {
			const updated = new Set(prev);
			if (initialBlocks && initialBlocks.length > 0) {
				const initial = new Set(initialBlocks.map((b) => b));
				if (!initial.has(time)) {
					return prev;
				}
			}
			if (updated.has(time)) {
				updated.delete(time);
			} else {
				updated.add(time);
			}
			return updated;
		});
	};

	return (
		<div style={{
			width: '100%',
			height: '100%',
			backgroundColor: '#f3f4f6',
			border: '1px solid #ccc',
			borderRadius: '8px',
			padding: '8px',
			boxSizing: 'border-box',
			display: 'flex',
			flexDirection: 'column',
			gap: '8px',
		}}>
			{!isEngineer && blockLabel && (
				<div style={{fontSize: '12px', fontWeight: 600, color: '#374151', textAlign: 'center'}}>
					Scheduled: {blockLabel}
				</div>
			)}
			{blocks.map((block) => {
				const isLocked = lockedBlocks.includes(block);
				const isSelected = selectedTimes.has(block);
				const isClickable = !disabled && !isLocked;
				const tooltipId = dayName ? `schedule-block-${dayName}-${block}` : null;
				const lockReason = lockReasons && lockReasons[block];

				return (
					<div key={block}>
						<div
							id={tooltipId || undefined}
							onClick={isClickable ? handleClick(block) : undefined}
							style={{
								backgroundColor: isLocked
									? '#d1d5db'
									: (isSelected ? (disabled ? '#60a5fa' : '#0062CC') : '#e5e7eb'),
								color: isLocked ? '#6b7280' : (isSelected ? 'white' : '#111827'),
								borderRadius: '4px',
								padding: '8px',
								fontSize: '14px',
								cursor: isClickable ? 'pointer' : 'not-allowed',
								opacity: isLocked ? 0.85 : 1,
							}}
						>
							{isEngineer ? `Appointment ${blocks.indexOf(block) + 1}` : officeBlockLabel(block)}
						</div>
						{tooltipId && isLocked && lockReason && (
							<UncontrolledTooltip placement="top" target={tooltipId}>
								{lockReason}
							</UncontrolledTooltip>
						)}
					</div>
				);
			})}
		</div>
	);
}
